import React, { useCallback, useState } from 'react'

import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { captureException } from '@sentry/react'
import { Button, useDataProvider, useNotify, useTranslate } from 'react-admin'
import { useImmer } from 'use-immer'

import { API } from '../../helpers/Api.helper'
import { Statement, StatementId } from '../../types/Statement'
import genericStyles from './../../generics.module.scss'
import styles from './RegenerateStatementsModal.module.scss'

type RegenerateStatementsModalProps = {
  statementIds: StatementId[]
  isOpen?: boolean
  onClose: (hasRegenerated: boolean) => void
}

type StatementValidation = {
  [id: StatementId]: boolean
}

export const RegenerateStatementsModal: React.FC<RegenerateStatementsModalProps> = ({
  statementIds,
  isOpen = false,
  onClose,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider()
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [statements, setStatements] = useState<Statement[]>([])
  const [statementsValidation, setStatementsValidation] = useImmer<StatementValidation>(
    statementIds.reduce(
      (acc, id) => ({
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        ...acc,
        [id]: false,
      }),
      {},
    ),
  )
  const [error, setError] = useState<string>()

  const closeModal = useCallback(
    (hasRegenerated: boolean) => {
      onClose(hasRegenerated)
      setError(undefined)
      setStatements([])
      setStatementsValidation({})
    },
    [onClose, setError, setStatements, setStatementsValidation],
  )

  React.useEffect(() => {
    const getStatements = async () => {
      setIsRegenerating(true)

      try {
        const selectedStatements = await dataProvider.getMany<Statement>('statements', {
          ids: statementIds,
        })
        setStatements(selectedStatements.data)
      } catch (e: any) {
        const message = e.response?.data?.message ?? e.response

        notify(`${message}`, { type: 'error' })
        console.error('Error - regenerate statement - get statements', e)
        setError(`${message} - ${e.response.data.content?.fieldError || e.response.data.content}`)
      }
    }

    if (isOpen) {
      getStatements()
    } else {
      setIsRegenerating(false)
    }
  }, [isOpen, dataProvider, statementIds, setStatements, notify])

  React.useEffect(() => {
    const regenerateStatements = async () => {
      const _sortedStatements = statements.sort((a, b) => {
        // always regenerate statements in chronological order as older will trigger regeneration of younger ones
        // regenerated younger ones will be returned by the API and skipped
        if (a.year === b.year) {
          return a.month - b.month
        }
        return a.year - b.year
      })
      let createdStatements: Statement[] = []
      for (let i = 0; i < _sortedStatements.length; i++) {
        const { id, beneficiaryId, month, year } = _sortedStatements[i]
        try {
          if (
            createdStatements.some(
              s => s.beneficiaryId === beneficiaryId && s.month === month && s.year === year,
            )
          ) {
            setStatementsValidation(stv => {
              stv[id] = true
            })
            continue
          }
          const job = (await API.post<any>('/api/statements/job', { beneficiaryId, month, year }))
            .data.job
          // await the job to be done
          const fetchJobStatus = async (): Promise<any> => {
            await new Promise(resolve => setTimeout(resolve, 1000))
            const up = (await API.get<any>(`/api/statements/jobStatus/${job.id}`)).data?.job
            if (up?.finishedOn) {
              return up
            }
            return fetchJobStatus()
          }
          const result = await fetchJobStatus()
          const _newStatements = result.returnvalue
          if (_newStatements.length === 0) {
            throw new Error('No statements created')
          }
          createdStatements = createdStatements.concat(_newStatements)
          setStatementsValidation(stv => {
            stv[id] = true
          })
        } catch (e: any) {
          captureException(e)
          const message = e.response?.data?.message || e.response?.data
          notify(`Statement ${month}/${year} - ${message}`, { type: 'error' })
          console.error('Error - regenerate statement', e)
          setError(
            `${message} - ${e.response?.data?.content?.fieldError || e.response?.data?.content}`,
          )
        }
      }
    }

    regenerateStatements()
    setIsRegenerating(false)
  }, [setIsRegenerating, statements, setStatementsValidation, notify])

  return (
    <Dialog aria-labelledby="youtube-import-modal" open={isOpen} onClose={() => closeModal(false)}>
      <DialogTitle id="youtube-import-modal">
        {translate('pages.beneficiaries.regenerateStatements')}
      </DialogTitle>
      <DialogContent dividers>
        <ul className={styles.list}>
          {statements.length > 0 &&
            statements.map(s => (
              <li key={s.id} className={styles.listItem}>
                {statementsValidation[s.id] ? (
                  <>&#9989;&nbsp;&nbsp;</>
                ) : (
                  <CircularProgress size="1rem" />
                )}
                <span className={styles.listDate}>
                  {s.month} / {s.year}
                </span>
              </li>
            ))}
        </ul>
        {error && (
          <p className={genericStyles.error}>
            <i>{translate('_generics.errorOccured')}</i>:&nbsp;
            <strong>{error}</strong>
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeModal(true)}
          label={translate('_generics.close')}
          disabled={isRegenerating}
        />
      </DialogActions>
    </Dialog>
  )
}
